import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MissionService } from '../../../../services/mission.service';
import moment from 'moment';
import 'moment/locale/fr';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-actual-working-hours',
  templateUrl: './actual-working-hours.component.html',
  styleUrls: ['./actual-working-hours.component.scss']
})
export class ActualWorkingHoursComponent implements OnInit {
  workingHoursForm: FormGroup;
  missionDates: moment.Moment[] = [];
  isLoading = true;
  establishmentName: string = '';
  serviceName: string = '';
  missionReference: string = '';

  constructor(
    private fb: FormBuilder,
    private missionService: MissionService,
    private dialogRef: MatDialogRef<ActualWorkingHoursComponent>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: {
      missionId: number,
      startDate: string,
      endDate: string,
      establishmentName?: string,
      serviceName?: string,
      missionReference?: string,
      workingHours?: any[],
      status?: string,
      // E3 (client clinique — P5) : heures théoriques de la mission (pré-remplissage)
      startTime?: string,
      endTime?: string,
      pauseTime?: number,
      dailyHours?: any[]
    }
  ) {
    this.workingHoursForm = this.fb.group({
      dailyWorkingHours: this.fb.array([])
    });

    // Initialiser les nouveaux champs
    this.establishmentName = this.data.establishmentName || '';
    this.serviceName = this.data.serviceName || '';
    this.missionReference = this.data.missionReference || '';

    // Générer les dates entre startDate et endDate
    const start = moment(this.data.startDate);
    const end = moment(this.data.endDate);
    let currentDate = start.clone();
    while (currentDate.isSameOrBefore(end)) {
      this.missionDates.push(currentDate.clone());
      this.addWorkingHoursDay(currentDate);
      currentDate.add(1, 'days');
    }

    // Charger les horaires existants
    this.loadExistingWorkingHours();
  }

  ngOnInit(): void {
    console.log("STATUS", this.data.status);
  }

  getFormattedDate(date: moment.Moment): string {
    return date.locale('fr').format('dddd D MMMM YYYY');
  }

  get dailyWorkingHours() {
    return this.workingHoursForm.get('dailyWorkingHours') as FormArray;
  }

  addWorkingHoursDay(date: moment.Moment) {
    // E3 : pré-remplissage — MissionDailyHours du jour sinon starttime/endtime + pauseTime de la mission.
    const dayKey = date.format('YYYY-MM-DD');
    const prefill = this.prefillForDay(dayKey);
    const dayForm = this.fb.group({
      workingDate: [dayKey],
      actualStartTime: [prefill.start, Validators.required],
      actualEndTime: [prefill.end, Validators.required],
      // Pause : TOUJOURS un nombre — 0 reste 0, null/absent s'affiche 0 (jamais un champ vide).
      actualPauseMinutes: [prefill.pause ?? 0],
      comment: [''],
      // E3 : valeur d'origine (affichée si le champ est modifié)
      originalStartTime: [prefill.start],
      originalEndTime: [prefill.end],
      originalPauseMinutes: [prefill.pause ?? 0]
    });
    this.dailyWorkingHours.push(dayForm);
  }

  /** E3 : heures proposées pour un jour — dailyHours[date] sinon starttime/endtime mission. */
  private prefillForDay(dayKey: string): { start: string, end: string, pause?: number } {
    let start: string = this.data.startTime || '';
    let end: string = this.data.endTime || '';
    const missionPause = this.data.pauseTime ?? undefined;
    let pause: number | undefined = missionPause;

    if (this.data.dailyHours && this.data.dailyHours.length) {
      const dh = this.data.dailyHours.find((d: any) => d && (d.date === dayKey || (d.date || '').toString().startsWith(dayKey)));
      if (dh) {
        if (dh.startTime) start = dh.startTime;
        if (dh.endTime) end = dh.endTime;

        // Une pause journalière à 0 ne doit pas écraser la pause prévue de la mission.
        // On privilégie la pause journalière seulement si elle est strictement positive,
        // sinon on retombe sur la pause de la mission (ou 0 si rien n'est prévu).
        if (dh.pauseTime !== undefined && dh.pauseTime !== null && dh.pauseTime > 0) {
          pause = dh.pauseTime;
        } else if (missionPause === undefined || missionPause === null) {
          pause = dh.pauseTime ?? 0;
        }
      }
    }

    // Guard : pause négative ou NaN → 0 (l'affichage doit toujours être un nombre).
    if (pause != null && (!isFinite(pause) || pause < 0)) pause = 0;
    return { start, end, pause: pause ?? 0 };
  }

  /** E3 : la valeur du champ a-t-elle été modifiée par rapport à l'origine ? */
  isModified(index: number, field: 'actualStartTime' | 'actualEndTime' | 'actualPauseMinutes'): boolean {
    const day = this.dailyWorkingHours.at(index);
    if (!day) return false;
    if (field === 'actualPauseMinutes') {
      const original = day.get('originalPauseMinutes')?.value;
      const current = day.get('actualPauseMinutes')?.value;
      // comparaison numérique : « null / absent » compte comme 0 (affichage 0 partout)
      return (original ?? 0) !== (current ?? 0);
    }
    const original = field === 'actualStartTime' ? day.get('originalStartTime')?.value : day.get('originalEndTime')?.value;
    const current = day.get(field)?.value;
    return !!original && !!current && this.normalizeTime(original) !== this.normalizeTime(current);
  }

  private normalizeTime(value: string): string {
    if (!value) return '';
    return value.length === 5 ? value : value.substring(0, 5);
  }

  /** E4 : libellé FR du statut du relevé (espace pro). */
  statusLabel(): string {
    switch (this.data.status) {
      case 'WAITING_AG_VALIDATION': return 'En attente de validation';
      case 'VALIDATED': return 'Validé';
      case 'REJECTED': return 'Refusé';
      default: return 'Brouillon';
    }
  }

  removeWorkingHoursDay(index: number) {
    this.dailyWorkingHours.removeAt(index);
  }

  private loadExistingWorkingHours() {
    this.isLoading = true;
    this.missionService.getActualWorkingHours(this.data.missionId).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          // Mettre à jour les formulaires avec les données existantes
          response.forEach((hours: any) => {
            const index = this.missionDates.findIndex(date =>
              date.format('YYYY-MM-DD') === hours.workingDate
            );
            if (index !== -1) {
              const dayForm = this.dailyWorkingHours.at(index);
              dayForm.patchValue({
                actualStartTime: hours.actualStartTime,
                actualEndTime: hours.actualEndTime,
                // null (pro n'a rien saisi) → 0 affiché ; jamais de champ vide
                actualPauseMinutes: hours.actualPauseMinutes ?? 0,
                comment: hours.comment,
                // E3 : valeurs d'origine renvoyées par le back (première saisie)
                originalStartTime: hours.originalStartTime || hours.actualStartTime,
                originalEndTime: hours.originalEndTime || hours.actualEndTime,
                originalPauseMinutes: hours.originalPauseMinutes ?? 0
              });
            }
          });
        }
        this.data.status = !!response && response.length > 0 ? response[0].status : 'DRAFT';
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading working hours:', error);
        this.snackBar.open('Erreur lors du chargement des horaires', 'Fermer', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  onSubmit(isSigning: boolean = false): void {
    if (this.workingHoursForm.valid) {
      if (isSigning) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent);

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.proceedWithSubmit(isSigning);
          }
        });
      } else {
        this.proceedWithSubmit(isSigning);
      }
    }
  }

  proceedWithSubmit(isSigning: boolean): void {
    const formValue = this.workingHoursForm.value;
    const payload = {
      missionId: this.data.missionId,
      // E3 : les champs original* sont purement front (affichage) — non envoyés au back.
      dailyWorkingHours: formValue.dailyWorkingHours.map((h: any) => ({
        workingDate: h.workingDate,
        actualStartTime: h.actualStartTime,
        actualEndTime: h.actualEndTime,
        // Pause toujours numérique : 0 affiché/pris comme 0, jamais null ni chaîne vide.
        actualPauseMinutes: Number(h.actualPauseMinutes) || 0,
        comment: ' '
      })),
      status: isSigning ? 'WAITING_AG_VALIDATION' : 'DRAFT'
    };

    let h = payload.dailyWorkingHours.map((v: any) => ({
      ...v,
      status: payload.status
    }));

    const request = isSigning
      ? this.missionService.signAndSubmitWorkingHours(this.auth.user?.collary_id!!, this.data.missionId, h, payload.status)
      : this.missionService.updateActualWorkingHours(this.auth.user?.collary_id!!, this.data.missionId, h, payload.status);

    request.subscribe({
      next: () => {
        const message = isSigning ? 'Horaires signés et envoyés avec succès' : 'Horaires enregistrés avec succès';
        this.snackBar.open(message, 'Fermer', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error:', error);
        const message = isSigning ? 'Erreur lors de la signature des horaires' : 'Erreur lors de l\'enregistrement des horaires';
        this.snackBar.open(message, 'Fermer', { duration: 3000 });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}